/**
 * 가격 및 결제 페이지
 */
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import * as PortOne from '@portone/browser-sdk/v2';
import { useAuth } from '../../contexts/AuthContext';
import { usePricing } from '../../contexts/PricingContext';
import { paymentAPI } from '../../services/api';
import { PricingCard } from '../../components/organisms/PricingCard/PricingCard';
import { ServiceGuideModal } from '../../components/organisms/ServiceGuideModal';
import { tokens } from '../../design-system/tokens';
import { SERVICE_NAMES, SERVICE_DESCRIPTIONS, SERVICE_FEATURES } from '../../constants/pricing';
import { isMobile } from '../../utils/device';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${tokens.spacing[10]} ${tokens.spacing[5]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[8]} ${tokens.spacing[4]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[6]} ${tokens.spacing[4]};
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${tokens.spacing[4]};
`;

const Title = styled.h1`
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[800]};
  text-align: center;
  margin-bottom: ${tokens.spacing[2]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize['2xl']};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xl};
  }
`;

const ServiceGuideLink = styled.button`
  background: none;
  border: none;
  color: ${tokens.colors.neutral[500]};
  font-size: ${tokens.typography.fontSize.sm};
  font-weight: ${tokens.typography.fontWeight.regular};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-bottom: ${tokens.spacing[4]};
  transition: color ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};

  &:hover {
    color: ${tokens.colors.primary[500]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const Subtitle = styled.p`
  font-size: ${tokens.typography.fontSize.lg};
  color: ${tokens.colors.neutral[600]};
  text-align: center;
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    font-size: ${tokens.typography.fontSize.base};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const InfoMessage = styled.p`
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.primary[500]};
  text-align: center;
  margin-bottom: ${tokens.spacing[8]};
  font-weight: ${tokens.typography.fontWeight.medium};
  word-break: keep-all;

  @media (max-width: ${tokens.breakpoints.md}) {
    margin-bottom: ${tokens.spacing[6]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[5]};
    font-size: ${tokens.typography.fontSize.xs};
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${tokens.spacing[4]};
  margin-bottom: ${tokens.spacing[8]};

  @media (max-width: ${tokens.breakpoints.sm}) {
    margin-bottom: ${tokens.spacing[6]};
    gap: ${tokens.spacing[3]};
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: ${tokens.spacing[3]} ${tokens.spacing[8]};
  border: none;
  border-radius: ${tokens.borderRadius.md};
  font-size: ${tokens.typography.fontSize.base};
  font-weight: ${tokens.typography.fontWeight.semibold};
  cursor: pointer;
  background: ${({ $active }) =>
    $active ? tokens.colors.primary[500] : tokens.colors.neutral[100]};
  color: ${({ $active }) => ($active ? tokens.colors.neutral[0] : tokens.colors.neutral[700])};
  transition: all 0.2s;

  &:hover {
    background: ${({ $active }) =>
      $active ? tokens.colors.primary[600] : tokens.colors.neutral[200]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    padding: ${tokens.spacing[2]} ${tokens.spacing[5]};
    font-size: ${tokens.typography.fontSize.sm};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${tokens.spacing[8]};
  margin-bottom: ${tokens.spacing[10]};

  @media (max-width: ${tokens.breakpoints.md}) {
    gap: ${tokens.spacing[6]};
    margin-bottom: ${tokens.spacing[8]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${tokens.spacing[5]};
    margin-bottom: ${tokens.spacing[6]};
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${tokens.spacing[6]};

  @media (max-width: ${tokens.breakpoints.md}) {
    gap: ${tokens.spacing[5]};
  }

  @media (max-width: ${tokens.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${tokens.spacing[4]};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${tokens.colors.neutral[200]};
  border-top-color: ${tokens.colors.primary[500]};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const PricingPage: React.FC = () => {
  const { user } = useAuth();
  const { servicePrices, subscriptionPrices, loading } = usePricing();
  const [activeTab, setActiveTab] = useState<'subscription' | 'oneTime'>('subscription');
  const [isServiceGuideOpen, setIsServiceGuideOpen] = useState(false);

  // 동적으로 구독 플랜 생성
  const subscriptionPlans = useMemo(() => [
    {
      title: '1일 이용권',
      price: subscriptionPrices['1day'],
      description: '24시간 모든 서비스 무제한 이용',
      features: ['모든 운세 서비스 무제한', '24시간 이용', '영구 보관'],
      productType: '1day',
      recommended: false
    },
    {
      title: '7일 이용권',
      price: subscriptionPrices['7days'],
      description: '7일간 모든 서비스 무제한 이용',
      features: ['모든 운세 서비스 무제한', '7일간 이용', '영구 보관', '약 44% 할인'],
      productType: '7days',
      recommended: true
    },
    {
      title: '30일 이용권',
      price: subscriptionPrices['30days'],
      description: '한 달간 모든 서비스 무제한 이용',
      features: ['모든 운세 서비스 무제한', '30일간 이용', '영구 보관', '약 67% 할인'],
      productType: '30days',
      recommended: false
    }
  ], [subscriptionPrices]);

  // 동적으로 일회성 구매 플랜 생성
  const oneTimePlans = useMemo(() => [
    {
      title: SERVICE_NAMES.today,
      price: servicePrices.today,
      description: SERVICE_DESCRIPTIONS.today,
      features: SERVICE_FEATURES.today,
      productType: 'today'
    },
    {
      title: SERVICE_NAMES.saju,
      price: servicePrices.saju,
      description: SERVICE_DESCRIPTIONS.saju,
      features: SERVICE_FEATURES.saju,
      productType: 'saju'
    },
    {
      title: SERVICE_NAMES.tojung,
      price: servicePrices.tojung,
      description: SERVICE_DESCRIPTIONS.tojung,
      features: SERVICE_FEATURES.tojung,
      productType: 'tojung'
    },
    {
      title: SERVICE_NAMES.compatibility,
      price: servicePrices.compatibility,
      description: SERVICE_DESCRIPTIONS.compatibility,
      features: SERVICE_FEATURES.compatibility,
      productType: 'compatibility'
    },
    {
      title: SERVICE_NAMES.wealth,
      price: servicePrices.wealth,
      description: SERVICE_DESCRIPTIONS.wealth,
      features: SERVICE_FEATURES.wealth,
      productType: 'wealth'
    },
    {
      title: SERVICE_NAMES.love,
      price: servicePrices.love,
      description: SERVICE_DESCRIPTIONS.love,
      features: SERVICE_FEATURES.love,
      productType: 'love'
    }
  ], [servicePrices]);

  const handlePurchase = async (productType: string, amount: number, paymentType: 'oneTime' | 'subscription') => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    try {
      // 1. 결제 생성 (Firebase Functions에서 merchantUid 생성)
      const response: any = await paymentAPI.create({
        paymentType,
        productType,
        amount
      });

      if (response.success) {
        const { merchantUid, productName, amount: paymentAmount } = response.data;

        // 2. returnPath만 sessionStorage에 저장 (redirect 후 원래 페이지로 복귀용)
        // merchantUid는 paymentId로 전달되므로 저장 불필요
        sessionStorage.setItem('payment_returnPath', window.location.pathname);

        // 3. PortOne V2 결제창 호출 (모바일: redirect, 데스크탑: callback)
        const portOneResponse = await PortOne.requestPayment({
          storeId: import.meta.env.VITE_PORTONE_STORE_ID,
          channelKey: import.meta.env.VITE_PORTONE_CHANNEL_KEY,
          paymentId: merchantUid,
          orderName: productName,
          totalAmount: paymentAmount,
          currency: 'CURRENCY_KRW',
          payMethod: 'CARD',
          customer: {
            email: user.email || undefined,
            phoneNumber: user.phoneNumber || undefined
          },
          // 모바일에서는 redirect 방식 사용 (redirectUrl 필수)
          ...(isMobile() && {
            redirectUrl: `${window.location.origin}/payment/redirect`
          }),
          // KCP V2 모바일 필수 파라미터
          bypass: {
            kcp_v2: {
              site_name: 'LuckyOn',  // 결제창에 표시될 상호명 (모바일 필수)
              shop_user_id: user.uid  // 리스크 관리용 회원 ID (상품권/휴대폰 결제 시 필수, 카드는 선택)
            }
          } as any
        });

        // 모바일인 경우 redirect되므로 여기까지 오지 않음
        // 데스크탑(callback 방식)인 경우에만 아래 로직 실행

        // 4. 결제 결과 처리 (데스크탑 callback 방식)
        // 사용자가 결제를 취소한 경우
        if (portOneResponse === undefined) {
          sessionStorage.removeItem('payment_returnPath');
          alert('결제가 취소되었습니다.');
          return;
        }

        // 결제 실패 (code가 있으면 오류)
        if (portOneResponse.code !== undefined) {
          console.error('결제 실패:', portOneResponse);
          sessionStorage.removeItem('payment_returnPath');
          alert(`결제 실패: ${portOneResponse.message || '알 수 없는 오류'}`);
          return;
        }

        // 5. 결제 성공 - 검증 및 완료 처리 (데스크탑)
        // PaymentResponse 타입에 따르면 paymentId는 항상 존재
        try {
          // 5-1. 결제 검증
          const verifyResponse: any = await paymentAPI.verify(
            portOneResponse.paymentId,
            merchantUid
          );

          if (verifyResponse.success) {
            // 5-2. 결제 완료 처리
            const completeResponse: any = await paymentAPI.complete(
              portOneResponse.paymentId,
              merchantUid
            );

            if (completeResponse.success) {
              // sessionStorage 정리
              sessionStorage.removeItem('payment_returnPath');

              // 결제 타입에 따라 적절한 페이지로 이동
              if (paymentType === 'subscription') {
                // 구독: 모든 서비스 이용 가능하므로 홈으로
                alert('결제가 완료되었습니다! 홈으로 이동합니다.');
                setTimeout(() => {
                  window.location.href = '/';
                }, 100);
              } else {
                // 일회성: 해당 서비스 페이지로 이동
                const serviceRoutes: Record<string, string> = {
                  today: '/fortune/today',
                  saju: '/fortune/saju',
                  tojung: '/fortune/tojung',
                  compatibility: '/fortune/compatibility',
                  wealth: '/fortune/wealth',
                  love: '/fortune/love'
                };
                const targetRoute = serviceRoutes[productType] || '/';
                alert(`결제가 완료되었습니다! ${targetRoute}로 이동합니다.`);
                setTimeout(() => {
                  window.location.href = targetRoute;
                }, 100);
              }
            } else {
              console.error('결제 완료 처리 실패:', completeResponse);
              alert('결제 완료 처리 중 오류가 발생했습니다.');
            }
          } else {
            console.error('결제 검증 실패:', verifyResponse);
            alert('결제 검증에 실패했습니다.');
          }
        } catch (error: any) {
          console.error('Payment verification error:', error);
          alert('결제 확인 중 오류가 발생했습니다: ' + error.message);
        }
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      alert('결제 생성 중 오류가 발생했습니다: ' + error.message);
    }
  };

  // 로딩 중일 때 스피너 표시
  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <TitleSection>
        <Title>요금제 선택</Title>
        <ServiceGuideLink onClick={() => setIsServiceGuideOpen(true)}>
          서비스 이용안내
        </ServiceGuideLink>
      </TitleSection>
      <Subtitle>AI가 분석하는 정확한 운세 서비스</Subtitle>
      <InfoMessage>보셨던 운세는 언제든지 마이페이지에서 다시 확인하실 수 있어요.</InfoMessage>

      <TabContainer>
        <Tab $active={activeTab === 'subscription'} onClick={() => setActiveTab('subscription')}>
          정기 이용권
        </Tab>
        <Tab $active={activeTab === 'oneTime'} onClick={() => setActiveTab('oneTime')}>
          개별 구매
        </Tab>
      </TabContainer>

      {activeTab === 'subscription' ? (
        <CardGrid>
          {subscriptionPlans.map((plan) => (
            <PricingCard
              key={plan.productType}
              title={plan.title}
              price={plan.price}
              description={plan.description}
              features={plan.features}
              productType={plan.productType}
              paymentType="subscription"
              recommended={plan.recommended}
              onPurchase={() => handlePurchase(plan.productType, plan.price, 'subscription')}
            />
          ))}
        </CardGrid>
      ) : (
        <ServiceGrid>
          {oneTimePlans.map((plan) => (
            <PricingCard
              key={plan.productType}
              title={plan.title}
              price={plan.price}
              description={plan.description}
              features={plan.features}
              productType={plan.productType}
              paymentType="oneTime"
              onPurchase={() => handlePurchase(plan.productType, plan.price, 'oneTime')}
            />
          ))}
        </ServiceGrid>
      )}

      <ServiceGuideModal
        isOpen={isServiceGuideOpen}
        onClose={() => setIsServiceGuideOpen(false)}
      />
    </Container>
  );
};